import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectGroup, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import {
  TrendingUp, TrendingDown, Users, IndianRupee, Award, Search,
  ArrowUpDown, ArrowUp, ArrowDown, Target, BarChart3, Layers, Download,
} from 'lucide-react';

import { API } from '@/utils/api';
import RegionProductMix from '@/components/RegionProductMix';
import TrendAnalysis from '@/components/TrendAnalysis';

// ─── Constants ──────────────────────────────────────────────
const REGION_COLORS = ['#818CF8', '#34D399', '#FBBF24', '#F87171', '#A78BFA', '#60A5FA', '#F472B6', '#2DD4BF'];

// ─── Utility functions ──────────────────────────────────────
const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '₹0';
  const absVal = Math.abs(value);
  if (absVal >= 10000000) {
    return `₹${(value / 10000000).toFixed(2)} Cr`;
  } else if (absVal >= 100000) {
    return `₹${(value / 100000).toFixed(2)} L`;
  } else if (absVal >= 1000) {
    return `₹${(value / 1000).toFixed(1)} K`;
  }
  return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatLakhs = (value) => {
  if (value == null || isNaN(value)) return '₹0 L';
  return `₹${(value / 100000).toFixed(2)} L`;
};

const GrowthBadge = ({ value }) => {
  if (value == null || isNaN(value)) return <Badge variant="secondary">--</Badge>;
  const isPositive = value > 0;
  const isZero = value === 0;
  return (
    <Badge
      variant="secondary"
      className={`text-xs font-medium ${
        isZero ? 'bg-gray-100 text-gray-600' :
        isPositive ? 'bg-green-50 text-green-700 border-green-200' :
        'bg-red-50 text-red-700 border-red-200'
      }`}
    >
      {isPositive ? '+' : isZero ? '' : ''}{value.toFixed(1)}%
    </Badge>
  );
};

// ─── Chart Tooltips ─────────────────────────────────────────
const ChartTooltipContent = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3 min-w-[180px]">
      <p className="text-xs font-semibold text-gray-600 mb-2 pb-2 border-b border-gray-100">{label}</p>
      <div className="space-y-1.5">
        {payload.map((entry, i) => {
          const dk = (entry.dataKey || '').toLowerCase();
          const isPercent = dk.includes('%') || dk.includes('growth') || dk.includes('cumulative');
          return (
            <div key={i} className="flex items-center justify-between gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
                <span className="text-gray-500 text-xs">{entry.name}</span>
              </div>
              <span className="font-semibold text-gray-900">
                {isPercent ? `${Number(entry.value).toFixed(1)}%` : `₹${Number(entry.value).toFixed(2)} L`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Inline bar for tables ──────────────────────────────────
const InlineBar = ({ value, max, color = '#818CF8', showValue = true }) => {
  const pct = max > 0 ? Math.min((Math.abs(value) / max) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-2">
      {showValue && <span className="text-sm font-medium min-w-[72px] text-right">{formatCurrency(value)}</span>}
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden min-w-[40px]">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
};

// ─── Sparkline (pure SVG — cheap enough for one per table row) ──
const Spark = ({ cur = [], prev = [], w = 96, h = 26 }) => {
  if (!cur?.length) return <div style={{ width: w, height: h }} />;
  const max = Math.max(1, ...cur, ...(prev || []));
  const pts = (arr) => arr.map((v, i) => `${(i / 11) * (w - 6) + 3},${h - 3 - (v / max) * (h - 8)}`).join(' ');
  let lastIdx = -1;
  cur.forEach((v, i) => { if (v > 0) lastIdx = i; });
  return (
    <svg width={w} height={h} className="overflow-visible flex-shrink-0">
      {prev?.length > 0 && <polyline points={pts(prev)} fill="none" stroke="#CBD5E1" strokeWidth="1.2" opacity="0.7" />}
      <polyline points={pts(cur)} fill="none" stroke="#6366F1" strokeWidth="1.6" />
      {lastIdx >= 0 && (
        <circle cx={(lastIdx / 11) * (w - 6) + 3} cy={h - 3 - (cur[lastIdx] / max) * (h - 8)} r="2.2" fill="#6366F1" />
      )}
    </svg>
  );
};

// Signed rupee delta, colored — the star metric across the redesign.
const DeltaAmount = ({ value, className = '' }) => {
  if (value == null || isNaN(value)) return <span className="text-gray-400">—</span>;
  const pos = value > 0;
  return (
    <span className={`font-semibold tabular-nums ${pos ? 'text-green-600' : value < 0 ? 'text-red-600' : 'text-gray-500'} ${className}`}>
      {pos ? '+' : ''}{formatCurrency(value)}
    </span>
  );
};

// ─── Heatmap color helper ───────────────────────────────────
const getHeatmapColor = (growth) => {
  if (growth == null || isNaN(growth)) return 'bg-gray-100 text-gray-500';
  if (growth >= 30) return 'bg-green-600 text-white';
  if (growth >= 15) return 'bg-green-500 text-white';
  if (growth >= 5) return 'bg-green-400 text-white';
  if (growth >= 0) return 'bg-green-100 text-green-800';
  if (growth >= -10) return 'bg-red-100 text-red-800';
  if (growth >= -20) return 'bg-red-400 text-white';
  return 'bg-red-600 text-white';
};

// ─── FY helpers ─────────────────────────────────────────────
const getCurrentFY = () => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};
const fyLabel = (y) => `FY ${y}-${String(y + 1).slice(-2)}`;

// From FY 2022-23 to the current FY — the list used to stop at 2026-27.
const fyOptions = Array.from({ length: getCurrentFY() - 2022 + 1 }, (_, i) => ({
  value: 2022 + i, label: fyLabel(2022 + i),
}));

const fyMonths = [
  { value: '4', label: 'Apr' }, { value: '5', label: 'May' }, { value: '6', label: 'Jun' },
  { value: '7', label: 'Jul' }, { value: '8', label: 'Aug' }, { value: '9', label: 'Sep' },
  { value: '10', label: 'Oct' }, { value: '11', label: 'Nov' }, { value: '12', label: 'Dec' },
  { value: '1', label: 'Jan' }, { value: '2', label: 'Feb' }, { value: '3', label: 'Mar' },
];

// ═════════════════════════════════════════════════════════════
// Tabs that bring their own period / filter bar (the page bar is hidden there).
const OWN_BAR_TABS = ['product_mix', 'trend_analysis'];

// How far the Sales Report runs, per division — figures here stop at the
// last invoice uploaded, so a month not uploaded yet is flagged (amber when a
// division is missing last month).
const dmy = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

function SalesReportThrough({ rows }) {
  if (!rows?.length) return null;
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevKey = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  const prevName = prev.toLocaleString('en-IN', { month: 'long' });
  const behind = rows.filter(r => (r.latest_invoice_date || '').slice(0, 7) < prevKey);
  return (
    <div className={`mt-3 inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-1.5 text-xs ${behind.length ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
      <span className="font-semibold">Sales Report invoices up to</span>
      {rows.map(r => {
        const late = behind.includes(r);
        return (
          <span key={r.division} title={r.last_uploaded ? `Last uploaded ${dmy(r.last_uploaded)}` : undefined}>
            {rows.length > 1 && <span className="text-muted-foreground">{r.division}: </span>}
            <b className={late ? 'text-amber-800' : 'text-slate-800'}>{dmy(r.latest_invoice_date)}</b>
          </span>
        );
      })}
      {behind.length > 0 && (
        <span className="font-semibold text-amber-800">
          · {prevName}&rsquo;s Sales Report isn&rsquo;t uploaded{rows.length > 1 ? ` for ${behind.map(r => r.division).join(', ')}` : ''} yet — figures stop there
        </span>
      )}
    </div>
  );
}

export default function Sales() {
  const { user, selectedDivision } = useOutletContext();
  const isAdmin = user?.role === 'admin';
  // Product Mix + Trend Analysis: admin + accounts, and sales members — whose
  // figures the server limits to their own customers (2026-10-29).
  const canSeeProductMix = isAdmin || user?.role === 'accounts' || user?.role === 'sales_member';
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(getCurrentFY());
  const [compareYear, setCompareYear] = useState(getCurrentFY() - 1);
  const [month, setMonth] = useState('all');
  const [region, setRegion] = useState('all');
  const [customerId, setCustomerId] = useState('all');
  const [likeToLike, setLikeToLike] = useState(false);
  const [data, setData] = useState(null);

  // Customer table state
  const [customerSearch, setCustomerSearch] = useState('');
  const [sortField, setSortField] = useState('current_sales');
  const [sortDir, setSortDir] = useState('desc');

  // Controlled tabs + customer segment chip, so Overview action cards and the
  // Region board can deep-link straight into a filtered Customer ledger.
  const [activeTab, setActiveTab] = useState('overview');
  const [customerSegment, setCustomerSegment] = useState('all');
  const [regionSort, setRegionSort] = useState('delta'); // delta | current_sales | growth

  // Section PDF — management-shareable report of the whole page, honouring
  // every active filter (served by /analytics/sales-detailed/export-pdf).
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadSectionPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await axios.get(`${API}/analytics/sales-detailed/export-pdf`, {
        params: {
          year, compare_year: compareYear,
          month: month !== 'all' ? parseInt(month) : undefined,
          region: region !== 'all' ? region : undefined,
          customer_id: customerId !== 'all' ? customerId : undefined,
          division: selectedDivision !== 'all' ? selectedDivision : undefined,
          like_to_like: likeToLike || undefined,
        },
        withCredentials: true, responseType: 'blob', timeout: 180000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SalesReport_${selectedDivision !== 'all' ? selectedDivision.replace(/[^A-Za-z0-9]+/g, '') : 'AllDivisions'}_FY${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report PDF downloaded — ready to share');
    } catch (err) {
      let msg = err.message;
      try {
        const t = await (err.response?.data?.text?.() || Promise.resolve(''));
        if (t) { try { msg = JSON.parse(t).detail || msg; } catch { msg = t.slice(0, 150); } }
      } catch {}
      toast.error(`Report failed: ${msg}`);
    } finally {
      setPdfBusy(false);
    }
  };

  const SEGMENT_SORTS = { quiet: ['prev_sales', 'desc'], slipping: ['delta', 'asc'], new: ['current_sales', 'desc'], growing: ['delta', 'desc'], key: ['rank', 'asc'], all: ['current_sales', 'desc'] };
  const jumpToSegment = (seg) => {
    setCustomerSegment(seg);
    const [f, d] = SEGMENT_SORTS[seg] || SEGMENT_SORTS.all;
    setSortField(f); setSortDir(d);
    setActiveTab('customers');
  };

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/sales-detailed`, {
        params: {
          year,
          compare_year: compareYear,
          month: month !== 'all' ? parseInt(month) : undefined,
          region: region !== 'all' ? region : undefined,
          customer_id: customerId !== 'all' ? customerId : undefined,
          division: selectedDivision !== 'all' ? selectedDivision : undefined,
          like_to_like: likeToLike || undefined,
        },
        withCredentials: true,
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch sales analytics:', error);
      toast.error('Failed to load sales analytics');
    } finally {
      setLoading(false);
    }
  }, [year, compareYear, month, region, customerId, selectedDivision, likeToLike]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setCompareYear(year - 1);
  }, [year]);

  // ─── Data Transformations ───────────────────────────────
  const monthlyComboData = useMemo(() => {
    if (!data?.monthly_comparison) return [];
    return data.monthly_comparison.map((m) => {
      const curr = m.current_year / 100000;
      const comp = m.compare_year / 100000;
      const diff = curr - comp;
      const growth = m.compare_year > 0 ? ((m.current_year - m.compare_year) / m.compare_year * 100) : null;
      return {
        name: m.month,
        [fyLabel(year)]: curr,
        [fyLabel(compareYear)]: comp,
        growth: growth != null ? parseFloat(growth.toFixed(1)) : null,
        diff: parseFloat(diff.toFixed(2)),
      };
    });
  }, [data, year, compareYear]);

  // ── Derivation layer ────────────────────────────────────────
  // Enriched customer rows: everything downstream (action cards, segment
  // chips, region attention counts, the ledger) reads from this one memo.
  const custRows = useMemo(() => {
    if (!data?.customer_breakdown?.length) return [];
    const now = Date.now();
    return data.customer_breakdown.map((c) => {
      const delta = (c.current_sales || 0) - (c.prev_sales || 0);
      const daysSince = c.last_billed ? Math.floor((now - new Date(c.last_billed + 'T00:00:00').getTime()) / 86400000) : null;
      // Gone quiet: nothing billed this FY though they bought last FY —
      // upgraded to "no invoice in 90+ days" when last_billed is known, so
      // early-FY months don't flag every seasonal buyer.
      const isQuiet = c.prev_sales > 0 && c.current_sales === 0 && (daysSince == null || daysSince > 90);
      const isSlipping = c.current_sales > 0 && c.prev_sales >= 50000 && c.growth <= -20;
      const isNew = (!c.prev_sales || c.prev_sales === 0) && c.current_sales > 0;
      const isGrowing = c.prev_sales > 0 && c.growth >= 20;
      return { ...c, delta, daysSince, isQuiet, isSlipping, isNew, isGrowing };
    });
  }, [data]);

  // Action segments for the Overview strip + Customer-tab chips.
  const segments = useMemo(() => {
    const quiet = custRows.filter((c) => c.isQuiet).sort((a, b) => b.prev_sales - a.prev_sales);
    const slipping = custRows.filter((c) => c.isSlipping).sort((a, b) => a.delta - b.delta);
    const fresh = custRows.filter((c) => c.isNew).sort((a, b) => b.current_sales - a.current_sales);
    const growing = custRows.filter((c) => c.isGrowing).sort((a, b) => b.delta - a.delta);
    return {
      quiet, slipping, new: fresh, growing,
      quietValue: quiet.reduce((s, c) => s + c.prev_sales, 0),
      slippingValue: slipping.reduce((s, c) => s + (c.prev_sales - c.current_sales), 0),
      newValue: fresh.reduce((s, c) => s + c.current_sales, 0),
      growingValue: growing.reduce((s, c) => s + c.delta, 0),
    };
  }, [custRows]);

  // Region momentum rows: ₹ delta, share shift, attention counts. Default
  // sort is by ₹ delta — "which state moved" — not by size.
  const regionRows = useMemo(() => {
    if (!data?.region_breakdown?.length) return [];
    const totalPrev = data.region_breakdown.reduce((s, r) => s + (r.prev_sales || 0), 0) || 1;
    const rows = data.region_breakdown.map((r) => {
      const inRegion = custRows.filter((c) => c.region === r.region);
      return {
        ...r,
        delta: (r.current_sales || 0) - (r.prev_sales || 0),
        shareDelta: (r.contribution_pct || 0) - ((r.prev_sales || 0) / totalPrev * 100),
        quietCount: inRegion.filter((c) => c.isQuiet).length,
        slippingCount: inRegion.filter((c) => c.isSlipping).length,
        newCount: inRegion.filter((c) => c.isNew).length,
      };
    });
    const dir = regionSort === 'growth' ? (a, b) => b.growth - a.growth
      : regionSort === 'current_sales' ? (a, b) => b.current_sales - a.current_sales
      : (a, b) => b.delta - a.delta;
    return rows.sort(dir);
  }, [data, custRows, regionSort]);

  // Filtered & sorted customer ledger: segment chip → search → sort.
  const filteredCustomers = useMemo(() => {
    let list = custRows;
    if (customerSegment === 'key') list = list.filter((c) => c.is_pareto);
    else if (customerSegment === 'quiet') list = list.filter((c) => c.isQuiet);
    else if (customerSegment === 'slipping') list = list.filter((c) => c.isSlipping);
    else if (customerSegment === 'new') list = list.filter((c) => c.isNew);
    else if (customerSegment === 'growing') list = list.filter((c) => c.isGrowing);
    if (customerSearch.trim()) {
      const q = customerSearch.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.customer_id.toLowerCase().includes(q) ||
          c.region.toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (aVal == null) aVal = sortDir === 'asc' ? Infinity : -Infinity;
      if (bVal == null) bVal = sortDir === 'asc' ? Infinity : -Infinity;
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return list;
  }, [custRows, customerSegment, customerSearch, sortField, sortDir]);

  const maxCustomerSales = useMemo(() => {
    if (!data?.customer_breakdown?.length) return 1;
    return Math.max(...data.customer_breakdown.map(c => c.current_sales));
  }, [data]);

  const maxRegionSales = useMemo(() => {
    if (!data?.region_breakdown?.length) return 1;
    return Math.max(...data.region_breakdown.map(r => r.current_sales));
  }, [data]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={12} className="ml-1 text-muted-foreground" />;
    return sortDir === 'asc' ? <ArrowUp size={12} className="ml-1" /> : <ArrowDown size={12} className="ml-1" />;
  };

  // ─── Loading / Empty ────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data || !data.summary) {
    return (
      <div className="p-8">
        <p className="text-muted-foreground">No sales data available</p>
      </div>
    );
  }

  const { summary, monthly_comparison = [], region_breakdown = [], filters = {}, pareto = {} } = data;

  // ═══════════════════════════════════════════════════════════
  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold text-foreground mb-1">Sales Analytics</h1>
        <p className="text-muted-foreground text-sm">
          Detailed sales comparison across periods, regions, and customers
        </p>
        {!OWN_BAR_TABS.includes(activeTab) && <SalesReportThrough rows={data.data_through} />}
      </div>

      {/* Product Mix and Trend Analysis carry their own period / view bar,
          so the page-level bar (which doesn't apply to them) steps aside —
          one bar per screen. */}
      {!OWN_BAR_TABS.includes(activeTab) && (<>
      {/* Filter Bar — grouped so it reads like a sentence:
          PERIOD (what am I looking at) · COMPARE (against what) · FILTERS (narrowed how) */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-white rounded-xl border border-border/60 px-4 py-2.5 shadow-sm mb-6">
        {/* Period */}
        <div className="flex items-center gap-2">
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">Period</span>
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="h-9 text-sm w-auto min-w-[115px] border-0 bg-indigo-50/70 hover:bg-indigo-100 font-semibold text-indigo-900">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fyOptions.map((fy) => (
                <SelectItem key={fy.value} value={fy.value.toString()}>{fy.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="h-9 text-sm w-auto min-w-[100px] border-0 bg-indigo-50/70 hover:bg-indigo-100 text-indigo-900">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Full year</SelectItem>
              {fyMonths.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label} only</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="h-7 w-px bg-border/60 hidden md:block" />
        {/* Compare */}
        <div className="flex items-center gap-2">
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">Compare with</span>
          <Select value={compareYear.toString()} onValueChange={(v) => setCompareYear(parseInt(v))}>
            <SelectTrigger className="h-9 text-sm w-auto min-w-[115px] border-0 bg-gray-50 hover:bg-gray-100">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fyOptions.filter((fy) => fy.value !== year).map((fy) => (
                <SelectItem key={fy.value} value={fy.value.toString()}>{fy.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1.5" title="Compare only the months elapsed so far in the current FY, so a part-year isn't judged against a full year">
            <Switch checked={likeToLike} onCheckedChange={setLikeToLike} id="ltl-switch" />
            <label htmlFor="ltl-switch" className="text-xs text-muted-foreground cursor-pointer">Like-to-like</label>
          </div>
        </div>
        <div className="h-7 w-px bg-border/60 hidden md:block" />
        {/* Filters */}
        <div className="flex items-center gap-2">
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">Filters</span>
          {filters.regions?.length > 0 && (
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="h-9 text-sm w-auto min-w-[110px] border-0 bg-gray-50 hover:bg-gray-100">
                <SelectValue placeholder="All Regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {filters.regions.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {filters.customers?.length > 0 && (
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="h-9 text-sm w-auto min-w-[130px] border-0 bg-gray-50 hover:bg-gray-100">
                <SelectValue placeholder="All Customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                {(() => {
                  const allC = [...filters.customers].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                  const groups = [
                    { label: 'A — F', test: n => n >= 'A' && n < 'G' },
                    { label: 'G — L', test: n => n >= 'G' && n < 'M' },
                    { label: 'M — R', test: n => n >= 'M' && n < 'S' },
                    { label: 'S — Z', test: n => n >= 'S' },
                  ];
                  return groups.map(g => {
                    const items = allC.filter(c => g.test((c.name || '')[0]?.toUpperCase()));
                    if (!items.length) return null;
                    return (
                      <SelectGroup key={g.label}>
                        <SelectLabel className="text-xs font-bold text-indigo-600">{g.label}</SelectLabel>
                        {items.map((c) => (
                          <SelectItem key={c.customer_id} value={c.customer_id}>
                            {c.name?.substring(0, 30) || 'Unknown'}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  });
                })()}
              </SelectContent>
            </Select>
          )}
          {(month !== 'all' || region !== 'all' || customerId !== 'all') && (
            <button className="text-xs text-muted-foreground hover:text-red-500"
              onClick={() => { setMonth('all'); setRegion('all'); setCustomerId('all'); }}>
              Clear
            </button>
          )}
        </div>
        {/* Share */}
        {['admin', 'accounts', 'sales_member'].includes(user?.role) && (
          <button
            className="ml-auto inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold disabled:opacity-60"
            onClick={downloadSectionPdf}
            disabled={pdfBusy}
            title="Download this entire section — KPIs, monthly, call sheet, region momentum, top customers — as a shareable PDF honouring the filters above"
          >
            <Download size={13} /> {pdfBusy ? 'Building…' : 'Share report (PDF)'}
          </button>
        )}
      </div>


      {/* Scope line — spells out exactly what every card/table below reflects,
          so a filter change is visibly acknowledged. */}
      <p className="text-[11px] text-muted-foreground -mt-3 mb-4">
        Showing:{' '}
        <span className="font-medium text-foreground">
          {month !== 'all'
            ? `${fyMonths.find((m) => m.value === month)?.label || month} ${parseInt(month, 10) >= 4 ? year : year + 1}`
            : `Full ${fyLabel(year)}`}
        </span>
        {' '}vs {month !== 'all'
          ? `${fyMonths.find((m) => m.value === month)?.label || month} ${parseInt(month, 10) >= 4 ? compareYear : compareYear + 1}`
          : fyLabel(compareYear)}
        {' · '}{selectedDivision && selectedDivision !== 'all' ? selectedDivision : 'All divisions'}
        {region !== 'all' && <> · {region}</>}
        {customerId !== 'all' && <> · {(data?.filters?.customers || []).find((c) => c.customer_id === customerId)?.name || 'Selected customer'}</>}
        {likeToLike && <> · like-to-like</>}
        {month !== 'all' && <span className="text-muted-foreground"> — the 12-month chart always shows the full FY</span>}
      </p>
      </>)}

      {/* ══════════════ TABS ══════════════ */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="inline-flex h-10 bg-muted/50 rounded-lg p-1 gap-0.5">
          <TabsTrigger value="overview" className="text-xs px-4 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><BarChart3 size={13} className="mr-1.5" />Overview</TabsTrigger>
          <TabsTrigger value="regions" className="text-xs px-4 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><Target size={13} className="mr-1.5" />Region-wise</TabsTrigger>
          <TabsTrigger value="customers" className="text-xs px-4 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><Users size={13} className="mr-1.5" />Customer-wise</TabsTrigger>
          {canSeeProductMix && (
            <TabsTrigger value="product_mix" className="text-xs px-4 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><Layers size={13} className="mr-1.5" />Product Mix</TabsTrigger>
          )}
          {canSeeProductMix && (
            <TabsTrigger value="trend_analysis" className="text-xs px-4 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"><BarChart3 size={13} className="mr-1.5" />Trend Analysis</TabsTrigger>
          )}
        </TabsList>

        {/* ════════ TAB 1: OVERVIEW ════════ */}
        <TabsContent value="overview" className="space-y-6">
          {/* Gradient KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Total Sales */}
            <Card className="border-0 shadow-md bg-gradient-to-br from-indigo-50 to-white">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-400 mb-1">Total Sales</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.current_year_sales)}</p>
                    <Separator className="my-2.5" />
                    <p className="text-xs text-gray-500">vs {formatCurrency(summary.prev_year_sales)} ({fyLabel(compareYear)})</p>
                  </div>
                  <div className="bg-indigo-100 text-indigo-600 p-3 rounded-xl flex-shrink-0 ml-3">
                    <IndianRupee size={22} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* MoM Growth */}
            <Card className={`border-0 shadow-md bg-gradient-to-br ${summary.mom_growth >= 0 ? 'from-green-50 to-white' : 'from-red-50 to-white'}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className={`text-[10px] font-semibold uppercase tracking-wider ${summary.mom_growth >= 0 ? 'text-green-400' : 'text-red-400'} mb-1`}>MoM Growth</p>
                    <p className="text-2xl font-bold text-gray-900">{summary.mom_growth?.toFixed(1)}%</p>
                    <Separator className="my-2.5" />
                    <p className="text-xs text-gray-500">{formatCurrency(summary.current_month_sales)} vs {formatCurrency(summary.prev_month_sales)}</p>
                  </div>
                  <div className={`p-3 rounded-xl flex-shrink-0 ml-3 ${summary.mom_growth >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                    {summary.mom_growth >= 0 ? <TrendingUp size={22} /> : <TrendingDown size={22} />}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* YoY Growth */}
            <Card className={`border-0 shadow-md bg-gradient-to-br ${summary.yoy_growth >= 0 ? 'from-emerald-50 to-white' : 'from-red-50 to-white'}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className={`text-[10px] font-semibold uppercase tracking-wider ${summary.yoy_growth >= 0 ? 'text-emerald-400' : 'text-red-400'} mb-1`}>YoY Growth</p>
                    <p className="text-2xl font-bold text-gray-900">{summary.yoy_growth?.toFixed(1)}%</p>
                    <Separator className="my-2.5" />
                    <p className="text-xs text-gray-500">{fyLabel(year)} vs {fyLabel(compareYear)}</p>
                  </div>
                  <div className={`p-3 rounded-xl flex-shrink-0 ml-3 ${summary.yoy_growth >= 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                    <Target size={22} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Avg Per Customer */}
            <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-400 mb-1">Avg / Customer</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.avg_per_customer)}</p>
                    <Separator className="my-2.5" />
                    <p className="text-xs text-gray-500">{summary.total_customers_billed} customers billed</p>
                  </div>
                  <div className="bg-blue-100 text-blue-600 p-3 rounded-xl flex-shrink-0 ml-3">
                    <Users size={22} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Top Customer */}
            <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-white">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-400 mb-1">Top Customer</p>
                    <p className="text-sm font-bold text-gray-900 truncate" title={summary.top_customer?.name}>
                      {summary.top_customer?.name || 'N/A'}
                    </p>
                    <Separator className="my-2.5" />
                    <p className="text-xs text-gray-500">{formatCurrency(summary.top_customer?.sales)}</p>
                  </div>
                  <div className="bg-amber-100 text-amber-600 p-3 rounded-xl flex-shrink-0 ml-3">
                    <Award size={22} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Simple Monthly Bar Chart — Current FY vs Compare FY */}
          <Card className="border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
                <BarChart3 size={18} className="text-indigo-500" />
                Monthly Sales — {fyLabel(year)} vs {fyLabel(compareYear)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {monthlyComboData.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">No data available</div>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={monthlyComboData} barGap={6} barCategoryGap="18%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="name" stroke="#94a3b8" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis stroke="#94a3b8" tickFormatter={(v) => `₹${v.toFixed(0)}L`} axisLine={false} tickLine={false} />
                    <RechartsTooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const curr = payload.find(p => p.dataKey === fyLabel(year));
                        const comp = payload.find(p => p.dataKey === fyLabel(compareYear));
                        const row = payload[0]?.payload || {};
                        return (
                          <div className="bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3 min-w-[200px]">
                            <p className="text-xs font-semibold text-gray-600 mb-2 pb-2 border-b border-gray-100">{label}</p>
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-400" />
                                  <span className="text-gray-500 text-xs">{fyLabel(year)}</span>
                                </div>
                                <span className="font-semibold">₹{curr?.value?.toFixed(2)} L</span>
                              </div>
                              <div className="flex items-center justify-between gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                                  <span className="text-gray-500 text-xs">{fyLabel(compareYear)}</span>
                                </div>
                                <span className="font-semibold">₹{comp?.value?.toFixed(2)} L</span>
                              </div>
                              <div className="border-t border-gray-100 pt-1.5 mt-1.5 flex items-center justify-between text-xs">
                                <span className="text-gray-500">Growth</span>
                                <span className={`font-bold ${row.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {row.growth != null ? `${row.growth >= 0 ? '+' : ''}${row.growth}%` : '—'}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-500">Difference</span>
                                <span className={`font-bold ${row.diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {row.diff != null ? `${row.diff >= 0 ? '+' : ''}₹${row.diff} L` : '—'}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: 16 }} />
                    <Bar isAnimationActive={false} dataKey={fyLabel(year)} fill="#818CF8" radius={[6, 6, 0, 0]} name={fyLabel(year)} />
                    <Bar isAnimationActive={false} dataKey={fyLabel(compareYear)} fill="#E2E8F0" radius={[6, 6, 0, 0]} name={fyLabel(compareYear)} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              {/* Delta ribbon — 12 heat-colored cells (signed ₹ diff in Lakhs)
                  under the chart. This IS the old Monthly tab's heatmap and its
                  table's only unique column, compressed into one row. */}
              {monthlyComboData.length > 0 && (
                <div className="grid grid-cols-12 gap-1 mt-2 px-1">
                  {monthlyComboData.map((m) => (
                    <div
                      key={m.name}
                      className={`rounded text-center py-1 text-[10px] font-semibold tabular-nums ${getHeatmapColor(m.growth)}`}
                      title={`${m.name}: ${fyLabel(year)} ₹${m[fyLabel(year)]?.toFixed(1)}L vs ${fyLabel(compareYear)} ₹${m[fyLabel(compareYear)]?.toFixed(1)}L · growth ${m.growth != null ? `${m.growth >= 0 ? '+' : ''}${m.growth}%` : '—'} · diff ${m.diff >= 0 ? '+' : ''}₹${m.diff}L`}
                    >
                      {m.diff >= 0 ? '+' : ''}{m.diff?.toFixed(0)}
                      <div className="text-[8px] font-normal opacity-80">{m.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* "This week's calls" action strip — every headline a rupee figure.
              Replaces the old Best/Weakest-month trivia. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Gone quiet */}
            <Card className="border-red-100 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] uppercase tracking-wider text-red-500 font-semibold">
                    {month !== 'all' ? "Didn't bill this month" : 'Gone quiet (90+ days)'}
                  </p>
                  <span className="text-[10px] font-bold bg-red-100 text-red-700 rounded-full px-2 py-0.5">{segments.quiet.length}</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(segments.quietValue)} <span className="text-xs font-normal text-gray-400">at stake</span></p>
                <div className="mt-2 space-y-1">
                  {segments.quiet.length === 0 ? (
                    <p className="text-[11px] text-gray-400">Nobody has gone quiet.</p>
                  ) : segments.quiet.slice(0, 3).map((c) => (
                    <button key={c.customer_id} className="w-full flex items-center justify-between text-xs hover:bg-red-50 rounded px-1.5 py-1 text-left"
                      onClick={() => setCustomerId(c.customer_id)} title={`Filter the page to ${c.name}`}>
                      <span className="truncate text-gray-700">{c.name}</span>
                      <span className="text-gray-500 flex-shrink-0 ml-2">{formatCurrency(c.prev_sales)}</span>
                    </button>
                  ))}
                </div>
                {segments.quiet.length > 3 && (
                  <button className="text-[11px] text-red-600 hover:underline mt-1.5" onClick={() => jumpToSegment('quiet')}>
                    View all {segments.quiet.length} →
                  </button>
                )}
              </CardContent>
            </Card>
            {/* Slipping */}
            <Card className="border-amber-100 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold">
                    {month !== 'all' ? 'Down vs same month last FY' : 'Slipping (−20%+)'}
                  </p>
                  <span className="text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">{segments.slipping.length}</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(segments.slippingValue)} <span className="text-xs font-normal text-gray-400">lost vs last FY</span></p>
                <div className="mt-2 space-y-1">
                  {segments.slipping.length === 0 ? (
                    <p className="text-[11px] text-gray-400">No one is slipping.</p>
                  ) : segments.slipping.slice(0, 3).map((c) => (
                    <button key={c.customer_id} className="w-full flex items-center justify-between text-xs hover:bg-amber-50 rounded px-1.5 py-1 text-left"
                      onClick={() => setCustomerId(c.customer_id)} title={`Filter the page to ${c.name}`}>
                      <span className="truncate text-gray-700">{c.name}</span>
                      <DeltaAmount value={c.delta} className="text-xs flex-shrink-0 ml-2" />
                    </button>
                  ))}
                </div>
                {segments.slipping.length > 3 && (
                  <button className="text-[11px] text-amber-600 hover:underline mt-1.5" onClick={() => jumpToSegment('slipping')}>
                    View all {segments.slipping.length} →
                  </button>
                )}
              </CardContent>
            </Card>
            {/* New accounts */}
            <Card className="border-green-100 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] uppercase tracking-wider text-green-600 font-semibold">New accounts</p>
                  <span className="text-[10px] font-bold bg-green-100 text-green-700 rounded-full px-2 py-0.5">{segments.new.length}</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(segments.newValue)} <span className="text-xs font-normal text-gray-400">of new business</span></p>
                <div className="mt-2 space-y-1">
                  {segments.new.length === 0 ? (
                    <p className="text-[11px] text-gray-400">No new accounts yet.</p>
                  ) : segments.new.slice(0, 3).map((c) => (
                    <button key={c.customer_id} className="w-full flex items-center justify-between text-xs hover:bg-green-50 rounded px-1.5 py-1 text-left"
                      onClick={() => setCustomerId(c.customer_id)} title={`Filter the page to ${c.name}`}>
                      <span className="truncate text-gray-700">{c.name}</span>
                      <span className="text-green-700 font-semibold flex-shrink-0 ml-2">{formatCurrency(c.current_sales)}</span>
                    </button>
                  ))}
                </div>
                {segments.new.length > 3 && (
                  <button className="text-[11px] text-green-600 hover:underline mt-1.5" onClick={() => jumpToSegment('new')}>
                    View all {segments.new.length} →
                  </button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Pareto — one sentence, not a card + chart */}
          {pareto?.key_accounts > 0 && (
            <p className="text-[11px] text-muted-foreground">
              <span className="font-semibold text-foreground">{pareto.key_accounts}</span> of {pareto.total_accounts} customers ({pareto.key_pct_of_total}%) produce 80% of revenue ·{' '}
              <button className="text-indigo-600 hover:underline" onClick={() => jumpToSegment('key')}>see key accounts →</button>
            </p>
          )}
        </TabsContent>

        {/* ════════ TAB 2: REGION MOMENTUM ════════ */}
        <TabsContent value="regions" className="space-y-4">
          {region === 'all' ? (
            <Card className="border-border/60 shadow-sm">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-heading font-semibold">Region Momentum</CardTitle>
                <div className="inline-flex rounded-lg border border-border/60 overflow-hidden text-xs">
                  {[['delta', 'By ₹ change'], ['current_sales', 'By sales'], ['growth', 'By growth']].map(([v, lbl]) => (
                    <button key={v} onClick={() => setRegionSort(v)}
                      className={`px-3 h-8 ${regionSort === v ? 'bg-indigo-600 text-white' : 'bg-white text-muted-foreground'}`}>{lbl}</button>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-200 text-xs uppercase tracking-wider text-gray-600">
                        <th className="text-left py-2.5 px-3 font-semibold">Region</th>
                        <th className="text-left py-2.5 px-3 font-semibold">Trend ({fyLabel(year)} vs ghost {fyLabel(compareYear)})</th>
                        <th className="text-left py-2.5 px-3 font-semibold min-w-[160px]">{fyLabel(year)} Sales</th>
                        <th className="text-right py-2.5 px-3 font-semibold">₹ Δ vs {fyLabel(compareYear)}</th>
                        <th className="text-right py-2.5 px-3 font-semibold">Growth</th>
                        <th className="text-right py-2.5 px-3 font-semibold">Share Δ</th>
                        <th className="text-right py-2.5 px-3 font-semibold">Customers</th>
                        <th className="text-right py-2.5 px-3 font-semibold">Needs attention</th>
                      </tr>
                    </thead>
                    <tbody>
                      {regionRows.length === 0 ? (
                        <tr><td colSpan={8} className="py-12 text-center text-muted-foreground">No region data</td></tr>
                      ) : regionRows.map((r, i) => {
                        const custDelta = r.customer_count - (r.prev_customer_count ?? r.customer_count);
                        return (
                          <tr key={r.region}
                            className={`border-b border-border/50 hover:bg-indigo-50/40 cursor-pointer ${i % 2 === 1 ? 'bg-gray-50/40' : ''}`}
                            onClick={() => setRegion(r.region)}
                            title={`Focus the whole page on ${r.region}`}>
                            <td className="py-2.5 px-3 font-medium text-gray-800">
                              <span className="inline-flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: REGION_COLORS[i % REGION_COLORS.length] }} />
                                {r.region}
                              </span>
                            </td>
                            <td className="py-2.5 px-3"><Spark cur={r.monthly} prev={r.monthly_prev} /></td>
                            <td className="py-2.5 px-3"><InlineBar value={r.current_sales} max={maxRegionSales} color={REGION_COLORS[i % REGION_COLORS.length]} /></td>
                            <td className="py-2.5 px-3 text-right"><DeltaAmount value={r.delta} /></td>
                            <td className="py-2.5 px-3 text-right"><GrowthBadge value={r.growth} /></td>
                            <td className={`py-2.5 px-3 text-right text-xs font-medium ${Math.abs(r.shareDelta) < 0.5 ? 'text-gray-400' : r.shareDelta > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {r.shareDelta > 0 ? '+' : ''}{r.shareDelta.toFixed(1)}pt
                            </td>
                            <td className="py-2.5 px-3 text-right text-gray-600">
                              {r.customer_count}
                              {custDelta !== 0 && (
                                <span className={`ml-1 text-[10px] ${custDelta > 0 ? 'text-green-600' : 'text-red-600'}`}>({custDelta > 0 ? '+' : ''}{custDelta})</span>
                              )}
                            </td>
                            <td className="py-2.5 px-3 text-right text-[11px]">
                              {(r.quietCount > 0 || r.slippingCount > 0) ? (
                                <button className="text-red-600 hover:underline"
                                  onClick={(e) => { e.stopPropagation(); setRegion(r.region); jumpToSegment(r.quietCount > 0 ? 'quiet' : 'slipping'); }}>
                                  {[r.quietCount > 0 ? `${r.quietCount} quiet` : null, r.slippingCount > 0 ? `${r.slippingCount} slipping` : null].filter(Boolean).join(' · ')}
                                </button>
                              ) : <span className="text-gray-300">—</span>}
                              {r.newCount > 0 && <span className="text-green-600 ml-1.5">+{r.newCount} new</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="px-4 py-2 text-[11px] text-muted-foreground border-t border-gray-100">Click a row to focus the whole page on that region.</p>
              </CardContent>
            </Card>
          ) : (
            /* FILTERED STATE — the region filter is active: show that region's
               summary row plus its call sheet, from the already-scoped data. */
            <>
              {regionRows.map((r) => (
                <Card key={r.region} className="border-border/60 shadow-sm">
                  <CardContent className="p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                    <span className="font-semibold text-gray-900">{r.region}</span>
                    <Spark cur={r.monthly} prev={r.monthly_prev} w={140} h={32} />
                    <span className="text-sm text-gray-700">{formatCurrency(r.current_sales)} <span className="text-xs text-gray-400">vs {formatCurrency(r.prev_sales)}</span></span>
                    <DeltaAmount value={r.delta} />
                    <GrowthBadge value={r.growth} />
                    <span className="text-xs text-gray-500">{r.customer_count} customers</span>
                    <button className="text-xs text-indigo-600 hover:underline ml-auto" onClick={() => setRegion('all')}>← All regions</button>
                  </CardContent>
                </Card>
              ))}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="border-red-100 shadow-sm">
                  <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-red-700">Call first in {region}</CardTitle></CardHeader>
                  <CardContent className="space-y-1">
                    {custRows.filter((c) => c.isQuiet || c.isSlipping).sort((a, b) => a.delta - b.delta).slice(0, 8).map((c) => (
                      <button key={c.customer_id} className="w-full flex items-center justify-between text-xs hover:bg-red-50 rounded px-2 py-1.5 text-left"
                        onClick={() => setCustomerId(c.customer_id)}>
                        <span className="truncate text-gray-700">{c.name}<span className="text-gray-400 ml-1.5">{c.isQuiet ? 'quiet' : 'slipping'}</span></span>
                        <span className="flex items-center gap-3 flex-shrink-0 ml-2">
                          <span className="text-gray-400">{formatCurrency(c.prev_sales)} → {formatCurrency(c.current_sales)}</span>
                          <DeltaAmount value={c.delta} className="text-xs" />
                        </span>
                      </button>
                    ))}
                    {custRows.filter((c) => c.isQuiet || c.isSlipping).length === 0 && (
                      <p className="text-xs text-gray-400 py-2">No accounts need a call in {region}.</p>
                    )}
                  </CardContent>
                </Card>
                <Card className="border-green-100 shadow-sm">
                  <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-green-700">Winning in {region}</CardTitle></CardHeader>
                  <CardContent className="space-y-1">
                    {custRows.filter((c) => c.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, 5).map((c) => (
                      <button key={c.customer_id} className="w-full flex items-center justify-between text-xs hover:bg-green-50 rounded px-2 py-1.5 text-left"
                        onClick={() => setCustomerId(c.customer_id)}>
                        <span className="truncate text-gray-700">{c.name}</span>
                        <span className="flex items-center gap-3 flex-shrink-0 ml-2">
                          <DeltaAmount value={c.delta} className="text-xs" />
                          <GrowthBadge value={c.growth} />
                        </span>
                      </button>
                    ))}
                    {custRows.filter((c) => c.delta > 0).length === 0 && (
                      <p className="text-xs text-gray-400 py-2">No gainers yet in {region}.</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* ════════ TAB 3: CUSTOMER-WISE ════════ */}
        <TabsContent value="customers" className="space-y-6">
          {/* Segment chip rail + search — the chips ARE the call list.
              Each chip shows count + the rupee number that matters for it,
              filters the ledger below, and sets a purposeful default sort. */}
          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: 'all', label: `All (${custRows.length})`, cls: 'indigo' },
              { key: 'key', label: `Key (${pareto?.key_accounts || 0})`, cls: 'amber' },
              { key: 'growing', label: `Growing (${segments.growing.length} · +${formatCurrency(segments.growingValue)})`, cls: 'green' },
              { key: 'slipping', label: `${month !== 'all' ? 'Down' : 'Slipping'} (${segments.slipping.length} · −${formatCurrency(segments.slippingValue)})`, cls: 'amber' },
              { key: 'quiet', label: `${month !== 'all' ? "Didn't bill" : 'Gone quiet'} (${segments.quiet.length} · ${formatCurrency(segments.quietValue)})`, cls: 'red' },
              { key: 'new', label: `New (${segments.new.length} · ${formatCurrency(segments.newValue)})`, cls: 'green' },
            ].map((chip) => (
              <button
                key={chip.key}
                onClick={() => jumpToSegment(chip.key)}
                className={`text-xs px-3 h-8 rounded-full border transition-colors ${
                  customerSegment === chip.key
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : chip.cls === 'red' ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                    : chip.cls === 'amber' ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                    : chip.cls === 'green' ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {chip.label}
              </button>
            ))}
            <div className="relative w-full sm:w-auto sm:min-w-[240px] sm:ml-auto">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search name, ID or region..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                className="pl-9 h-8"
              />
            </div>
          </div>

          {/* Pareto microcopy */}
          {pareto?.key_accounts > 0 && (
            <p className="text-[11px] text-muted-foreground -mt-2">
              <span className="font-semibold text-foreground">{pareto.key_accounts}</span> of {pareto.total_accounts} customers ({pareto.key_pct_of_total}%) produce 80% of revenue.
            </p>
          )}

          {/* The Ledger — sortable, with the action columns: signed ₹ change,
              share shift, last billed, and a status chip. Row click focuses
              the whole page on that customer via the top filter. */}
          <Card className="border-border/60 shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-3 font-semibold text-gray-600 text-xs uppercase tracking-wider w-12">#</th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-600 text-xs uppercase tracking-wider cursor-pointer select-none hover:text-gray-900" onClick={() => toggleSort('name')}>
                        <span className="inline-flex items-center">Customer <SortIcon field="name" /></span>
                      </th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-600 text-xs uppercase tracking-wider cursor-pointer select-none hover:text-gray-900" onClick={() => toggleSort('region')}>
                        <span className="inline-flex items-center">Region <SortIcon field="region" /></span>
                      </th>
                      <th className="text-left py-3 px-3 font-semibold text-gray-600 text-xs uppercase tracking-wider cursor-pointer select-none hover:text-gray-900 min-w-[170px]" onClick={() => toggleSort('current_sales')}>
                        <span className="inline-flex items-center">{fyLabel(year)} <SortIcon field="current_sales" /></span>
                      </th>
                      <th className="text-right py-3 px-3 font-semibold text-gray-600 text-xs uppercase tracking-wider cursor-pointer select-none hover:text-gray-900" onClick={() => toggleSort('delta')}>
                        <span className="inline-flex items-center justify-end">₹ Change <SortIcon field="delta" /></span>
                      </th>
                      <th className="text-right py-3 px-3 font-semibold text-gray-600 text-xs uppercase tracking-wider cursor-pointer select-none hover:text-gray-900" onClick={() => toggleSort('prev_sales')}>
                        <span className="inline-flex items-center justify-end">{fyLabel(compareYear)} <SortIcon field="prev_sales" /></span>
                      </th>
                      <th className="text-right py-3 px-3 font-semibold text-gray-600 text-xs uppercase tracking-wider cursor-pointer select-none hover:text-gray-900" onClick={() => toggleSort('growth')}>
                        <span className="inline-flex items-center justify-end">Growth <SortIcon field="growth" /></span>
                      </th>
                      <th className="text-right py-3 px-3 font-semibold text-gray-600 text-xs uppercase tracking-wider cursor-pointer select-none hover:text-gray-900" onClick={() => toggleSort('contribution_pct')}>
                        <span className="inline-flex items-center justify-end">Share <SortIcon field="contribution_pct" /></span>
                      </th>
                      {customerSegment === 'key' && (
                        <th className="text-right py-3 px-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Cumul.</th>
                      )}
                      <th className="text-right py-3 px-3 font-semibold text-gray-600 text-xs uppercase tracking-wider cursor-pointer select-none hover:text-gray-900" onClick={() => toggleSort('last_billed')}>
                        <span className="inline-flex items-center justify-end">Last billed <SortIcon field="last_billed" /></span>
                      </th>
                      <th className="text-right py-3 px-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="py-12 text-center text-muted-foreground">
                          {customerSearch || customerSegment !== 'all' ? 'No customers match this view' : 'No customer data available'}
                        </td>
                      </tr>
                    ) : (
                      filteredCustomers.map((c, idx) => (
                        <tr
                          key={c.customer_id}
                          className={`border-b border-border/50 hover:bg-indigo-50/40 cursor-pointer transition-colors ${idx % 2 === 1 ? 'bg-gray-50/40' : ''}`}
                          onClick={() => setCustomerId(c.customer_id)}
                          title={`Focus the whole page on ${c.name}`}
                        >
                          <td className="py-2.5 px-3 text-gray-400 text-xs">{c.rank}</td>
                          <td className="py-2.5 px-3 font-medium truncate max-w-[200px]" title={c.name}>{c.name}</td>
                          <td className="py-2.5 px-3 text-gray-500 text-xs">{c.region}</td>
                          <td className="py-2.5 px-3">
                            <InlineBar value={c.current_sales} max={maxCustomerSales} color="#818CF8" />
                          </td>
                          <td className="py-2.5 px-3 text-right"><DeltaAmount value={c.delta} className="text-xs" /></td>
                          <td className="py-2.5 px-3 text-right text-gray-500">{formatCurrency(c.prev_sales)}</td>
                          <td className="py-2.5 px-3 text-right"><GrowthBadge value={c.growth} /></td>
                          <td className="py-2.5 px-3 text-right text-xs">
                            <span className="font-semibold text-gray-700">{c.contribution_pct}%</span>
                            {c.share_change != null && Math.abs(c.share_change) >= 0.1 && (
                              <span className={`ml-1 ${c.share_change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {c.share_change > 0 ? '▲' : '▼'}{Math.abs(c.share_change).toFixed(1)}
                              </span>
                            )}
                          </td>
                          {customerSegment === 'key' && (
                            <td className="py-2.5 px-3 text-right text-gray-400 text-xs">{c.cumulative_pct}%</td>
                          )}
                          <td className="py-2.5 px-3 text-right text-xs text-gray-500">
                            {c.last_billed
                              ? new Date(c.last_billed + 'T00:00:00').toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })
                              : '—'}
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            {c.isQuiet ? <span className="text-[10px] font-semibold text-red-700 bg-red-100 px-1.5 py-0.5 rounded-full">QUIET</span>
                              : c.isNew ? <span className="text-[10px] font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">NEW</span>
                              : c.isSlipping ? <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">SLIPPING</span>
                              : c.is_pareto ? <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">KEY</span>
                              : null}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {filteredCustomers.length > 0 && (
                <div className="px-4 py-2.5 border-t border-gray-200 text-xs text-gray-500 bg-gray-50/50">
                  Showing {filteredCustomers.length} customers · click a row to focus the page on that account
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>



        {/* ════════ TAB 5: PRODUCT MIX (admin only) ════════ */}
        {canSeeProductMix && (
          <TabsContent value="product_mix" className="space-y-6">
            <RegionProductMix selectedDivision={selectedDivision} />
          </TabsContent>
        )}

        {/* ════════ TAB 6: TREND ANALYSIS (admin only) ════════ */}
        {/* Standalone admin-only view: period summary + head-fabric
            tiles + per-category Top-10 performers with PYSP & rolling
            365d trend metrics. Backed by the same
            /analytics/region-product-mix endpoint as Product Mix —
            include_trends=true gives PYSP + 365d enrichment. */}
        {canSeeProductMix && (
          <TabsContent value="trend_analysis" className="space-y-6">
            <TrendAnalysis selectedDivision={selectedDivision} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
